/**
 * "View as" — `studio/client-detail.html`'s **Impersonate** button.
 *
 * `POST /api/studio/_impersonate` is one of the four studio routes that is not an
 * action (`.docs/20` §Design decisions), and it is not one for a reason worth
 * repeating here: impersonating somebody produces a **session cookie**, and there is
 * no shape of `defineAction` that returns a `Set-Cookie`. So this is the one place in
 * the studio portal that calls `api.fetch` rather than `useStudioAction`, and the
 * §DRY watchlist's "`useStudioAction` is the only way a studio page calls the API" has
 * exactly this exception.
 *
 * Three things it owns:
 *
 *   1. **Who.** The route takes an email, not a user id, and the target must hold a
 *      seat on the client being named — a mistyped address is a 404 rather than a
 *      session as somebody else's customer. The picker is the account's own members,
 *      so there is nothing to mistype.
 *   2. **The confirmation.** Everything the reader does next is recorded as that
 *      person, in that client's audit log, and both ends are written
 *      (`impersonation_start` / `impersonation_end`). A dialog naming the person is
 *      the smallest honest version of telling them that.
 *   3. **What happens after.** The cookie is the client's, so the *whole cache is
 *      wrong* — every studio query on screen was answered for staff. The page is
 *      reloaded onto `/app` rather than navigated: a router navigation would keep
 *      React Query's cache, and the first thing the client shell would render is the
 *      studio's own numbers under the impersonation banner.
 *
 * **An archived client has no button.** `.docs/20` §Edge cases: "no Impersonate for
 * archived", and `.docs/19`'s own rule for a control somebody may not press is
 * "buttons absent, not disabled" — a disabled Impersonate would be an invitation to
 * ask why. `archived` is **required** rather than optional for the reason the old
 * optional `disabled` was worth removing: nobody passed it, so the rule this header
 * claimed was never once applied. It is `account.archived_at !== null`, and it is not
 * "no live retainer" — a prospect has no retainer and is not archived, and an
 * archived account may still hold a retainer row.
 */

import { useState } from 'react'

import { useApi } from '../../api/provider.js'
import { CLIENT_PORTAL, leaveTheSpa } from '../../integrations/navigate.js'
import { messageOf, problemOf } from '../../api/problem.js'
import { Button } from '../portal/Button.js'
import { Select, useZodForm } from '../portal/Form.js'
import { Stack } from '../portal/Layout.js'
import { ConfirmDialog } from '../portal/Overlay.js'
import { Notice } from '../portal/Status.js'
import { Prose } from '../portal/Text.js'
import { z } from 'zod'

export type ImpersonateMember = {
  readonly email: string
  readonly name: string
  readonly role: string
}

export type ImpersonateButtonProps = {
  readonly client: string
  /** Everybody holding a seat. `clients_get_detail`'s `contacts.members`. */
  readonly members: readonly ImpersonateMember[]
  /** `account.archived_at !== null`. A closed account has nobody to view it as. */
  readonly archived: boolean
}

const FormSchema = z.object({ email: z.string().min(1, 'Choose somebody to view as.') })

/**
 * What the dialog promises about what you will not be able to do — the whole rule,
 * not one instance of it.
 *
 * It said "Money cannot be approved while you are viewing as somebody", which is
 * `ARCHITECTURE.md`'s sentence and is **narrower than the code**. Session 24 read the
 * rule off the services that decide and found it is *a decision that binds the
 * client*: approving a quote, an extra or a package change, declining any of the
 * three, giving or withdrawing a standing overage approval, signing off a content
 * plan — which carries no money at all — approving a deliverable, and revealing a
 * delegated credential. A reader who was promised only the money half and then found
 * *Sign off* disabled would reasonably file a bug, and the S-1 finding is the other
 * direction: *Approve* on a deliverable was **not** disabled, and the sentence gave
 * nobody a reason to expect that it should be.
 *
 * Ending with what is still open matters as much as what is not: comments, requests
 * and asking for changes are the reason to view as somebody in the first place.
 */
export const IMPERSONATION_LIMIT =
  'You will not be able to take a decision that binds them — approving or declining anything, ' +
  'signing work off, or opening one of their passwords. The portal refuses those and records ' +
  'the attempt. Reading, commenting, raising a request and asking for changes are all still ' +
  'yours to do.'

export function ImpersonateButton({ client, members, archived }: ImpersonateButtonProps) {
  const api = useApi()
  const [open, setOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [failure, setFailure] = useState<string | null>(null)

  const form = useZodForm(FormSchema, {
    defaultValues: { email: members[0]?.email ?? '' },
  })
  const email = form.watch('email')
  const chosen = members.find((one) => one.email === email)

  if (archived || members.length === 0) return null

  const start = async () => {
    setBusy(true)
    setFailure(null)
    try {
      const response = await api.fetch('/api/studio/_impersonate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ client, email }),
      })
      if (!response.ok) {
        setFailure(messageOf(await problemOf(response)))
        return
      }
      /*
       * A full load, not `navigate()`. See the header: the session on the wire is now
       * the client's and every answer in the cache was given to staff.
       */
      leaveTheSpa(CLIENT_PORTAL)
    } catch {
      setFailure('We could not reach the portal. Nothing has changed.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <>
      <Button
        onClick={() => {
          setOpen(true)
        }}
      >
        Impersonate
      </Button>
      <ConfirmDialog
        open={open}
        onOpenChange={(next) => {
          setOpen(next)
          if (!next) setFailure(null)
        }}
        title="View the portal as this client"
        confirmLabel={chosen === undefined ? 'View as them' : `View as ${chosen.name}`}
        cancelLabel="Stay in the studio"
        busy={busy}
        onConfirm={() => {
          void start()
        }}
      >
        <Stack>
          <Select
            control={form.control}
            name="email"
            label="Who to view as"
            hint="They have to hold a seat on this account."
            options={members.map((one) => ({
              value: one.email,
              label: `${one.name} · ${one.role}`,
            }))}
          />
          <Prose>
            Everything you do next is recorded as {chosen?.name ?? 'them'}, in this client’s own
            log, and the start and the end of it are both written down. {IMPERSONATION_LIMIT}
          </Prose>
          {failure === null ? null : (
            <Notice need role="alert">
              {failure}
            </Notice>
          )}
        </Stack>
      </ConfirmDialog>
    </>
  )
}
